/**
 * 期限卡「调整开始日期」对话框（ADM-24 子视图，权限点 ADM-24:PERIOD_ADJUST，
 * period-pass-admin-ui:SC-12~SC-14）。
 *
 * 待激活卡指定日期即生效；生效中卡平移区间（到期日 = 新开始日 + 期限天数 − 1 随动重算，含当天）；
 * 已到期卡不可调（入口隐藏 + 服务端 409 双层校验）。调整写 ADJUST 流水与操作日志审计。
 * - 提交防重复：确认按钮 confirmLoading；
 * - 409（已到期/乐观锁冲突）与 400（原因缺失/超长）透传服务端中文提示并保留表单；
 * - 动作级 RUM：action: entitlement.periodAdjust（attribute 仅 passId/studentId 白名单）。
 */
import { useMemo, useState } from 'react';
import { Alert, DatePicker, Descriptions, Input, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import dayjs, { type Dayjs } from 'dayjs';
import { DraggableModal } from '../../components/DraggableModal';
import {
  entitlementApi,
  type PeriodPassView,
} from '../../api/entitlement.api';
import { ApiError } from '../../api/transport';
import { startAction } from '../../observability/otel';
import {
  periodPassDisplayStatusLabel,
  periodPassStatusColor,
} from '../../utils/period-ledger';

const { Text } = Typography;

export interface PeriodAdjustModalProps {
  /** 待调整的期限卡（列表选中项；仅未到期卡可达此处） */
  pass: PeriodPassView;
  /** 所属学生 ID（RUM 白名单 attribute） */
  studentId: string;
  onClose: () => void;
  /** 调整成功回调（宿主关闭弹窗并即时刷新卡片列表与流水） */
  onAdjusted: () => void;
}

/** 期限卡「调整开始日期」对话框（高风险写操作，含只读摘要/到期预览/原因必填）。 */
export function PeriodAdjustModal({
  pass,
  studentId,
  onClose,
  onAdjusted,
}: PeriodAdjustModalProps) {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    pass.startDate ? dayjs(pass.startDate) : dayjs(),
  );
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 到期日自动预览（新开始日 + 期限天数 − 1，含当天，只读随动）
  const endDatePreview = useMemo(
    () =>
      startDate
        ? startDate.add(pass.periodDays - 1, 'day').format('YYYY-MM-DD')
        : undefined,
    [startDate, pass.periodDays],
  );

  async function submit() {
    if (!startDate) {
      message.error('请选择新开始日期');
      return;
    }
    if (!reason.trim()) {
      // 前端拦截：原因必填，不发请求（period-pass-admin-ui:SC-14①）
      message.error('请填写调整原因');
      return;
    }
    setSubmitting(true);
    // 动作级 RUM（frontend-rum §1.2 P0 教务链路）：attribute 仅 passId/studentId 白名单
    const span = startAction('action: entitlement.periodAdjust', {
      passId: pass.periodPassId,
      studentId,
    });
    try {
      const result = await entitlementApi.adjustStartDate(pass.periodPassId, {
        startDate: startDate.format('YYYY-MM-DD'),
        reason: reason.trim(),
        version: pass.version,
      });
      span.end();
      message.success(
        `已调整：${result.startDate} ~ ${result.endDate}（ADJUST 流水与操作日志已留痕）`,
      );
      onAdjusted();
    } catch (err) {
      span.end();
      // 409（已到期/乐观锁冲突）/400（原因缺失/超长）透传中文提示并保留表单（SC-14②）
      message.error(err instanceof ApiError ? err.message : '调整失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal
      open
      width={560}
      title="调整期限卡开始日期"
      onCancel={onClose}
      onOk={() => void submit()}
      okText="确认调整"
      confirmLoading={submitting}
      cancelText="取消"
      destroyOnHidden
      data-testid="period-adjust-modal"
    >
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="学生">{pass.studentName ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="课程SKU">{pass.skuName}</Descriptions.Item>
        <Descriptions.Item label="当前状态">
          <Tag color={periodPassStatusColor(pass.displayStatus)}>
            {periodPassDisplayStatusLabel[pass.displayStatus] ?? pass.displayStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="期限天数">{pass.periodDays} 天</Descriptions.Item>
        <Descriptions.Item label="当前区间" span={2}>
          {pass.startDate && pass.endDate
            ? `${pass.startDate} ~ ${pass.endDate}`
            : '待激活（未起算，首次上课自动起算）'}
        </Descriptions.Item>
        <Descriptions.Item label="来源订单" span={2}>
          {pass.sourceOrderId}
        </Descriptions.Item>
      </Descriptions>
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary">新开始日期（业务日期，可指定未来日期）</Text>
        <div>
          <DatePicker
            style={{ width: '100%', marginTop: 4 }}
            value={startDate}
            onChange={setStartDate}
            allowClear={false}
            data-testid="period-adjust-date"
          />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary">调整后到期日（系统计算，含当天）</Text>
        <div style={{ marginTop: 4 }}>
          <Text strong data-testid="period-adjust-end-preview">
            {startDate && endDatePreview
              ? `${startDate.format('YYYY-MM-DD')} ~ ${endDatePreview}`
              : '-'}
          </Text>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">调整原因（必填，≤100 字）</Text>
        <Input.TextArea
          style={{ marginTop: 4 }}
          rows={2}
          maxLength={100}
          showCount
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="例如：家长出行协商顺延起算 / 双方协商提前起算"
          data-testid="period-adjust-reason"
        />
      </div>
      <Alert
        type="warning"
        showIcon
        message="高风险操作"
        description="调整将写 ADJUST 流水与操作日志审计；待激活卡设日期即生效；生效中卡平移区间（到期日随动重算）；已到期卡不可调整。"
      />
    </DraggableModal>
  );
}
